import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import StepIndicator from '../components/guided/StepIndicator';
import Step1Organisation from '../components/guided/Step1Organisation';
import Step2Infrastructure from '../components/guided/Step2Infrastructure';
import Step3MonitoringNeeds from '../components/guided/Step3MonitoringNeeds';
import Step4Compliance from '../components/guided/Step4Compliance';
import Step5Results from '../components/guided/Step5Results';

export interface GuidedState {
  // Step 1
  sector: 'nhs' | 'fca' | 'public_sector' | 'private';
  organisationSize: 'small' | 'medium' | 'large';
  // Step 2
  windowsServers: number;
  linuxServers: number;
  desktops: number;
  azureVMs: number;
  firewalls: number;
  sqlDatabases: number;
  appServices: number;
  // Step 3
  needsSecurity: boolean;
  needsCompliance: boolean;
  needsPerformance: boolean;
  needsApplicationLogs: boolean;
  sentinelEnabled: boolean;
  // Step 4
  retentionDays: number;
  archiveYears: number;
  m365Licences: number;
  defenderServersP2: boolean;
}

const DEFAULT_STATE: GuidedState = {
  sector: 'private',
  organisationSize: 'medium',
  windowsServers: 0,
  linuxServers: 0,
  desktops: 0,
  azureVMs: 0,
  firewalls: 0,
  sqlDatabases: 0,
  appServices: 0,
  needsSecurity: false,
  needsCompliance: false,
  needsPerformance: false,
  needsApplicationLogs: false,
  sentinelEnabled: false,
  retentionDays: 90,
  archiveYears: 0,
  m365Licences: 0,
  defenderServersP2: false,
};

export default function GuidedWalkthroughPage() {
  const [step, setStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(new Set());
  const [state, setState] = useState<GuidedState>(DEFAULT_STATE);

  const handleChange = (updates: Partial<GuidedState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const goNext = () => {
    setCompletedSteps((prev) => new Set([...prev, step]));
    setStep((s) => Math.min(5, s + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setStep((s) => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleStepClick = (n: number) => {
    if (completedSteps.has(n) || n === step) setStep(n);
  };

  const handleReset = () => {
    setState(DEFAULT_STATE);
    setStep(1);
    setCompletedSteps(new Set());
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-base)' }}>
      <Helmet>
        <title>Azure Monitoring Cost Estimator | AzureCalc.uk</title>
        <meta name="description" content="Guided 5-step estimator for Azure Log Analytics and Sentinel costs. Answer questions about your infrastructure to get a tailored UK South cost estimate." />
        <link rel="canonical" href="https://www.azure-calc.co.uk/guided" />
        <meta name="robots" content="noindex" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div className="max-w-[680px] mx-auto px-4 py-8">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-[12px] mb-6 transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
        >
          <span className="material-symbols-outlined text-[16px]">arrow_back</span>
          Back to calculators
        </Link>

        {/* Page header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-[18px]">✨</span>
            <span className="text-[11px] font-bold uppercase tracking-widest text-[#0078d4]">Guided Estimator</span>
          </div>
          <h1 className="text-[28px] font-bold leading-tight" style={{ color: 'var(--text-primary)' }}>
            Azure Monitoring Cost Estimator
          </h1>
          <p className="text-[14px] mt-1" style={{ color: 'var(--text-tertiary)' }}>
            Answer five simple questions to get a tailored Log Analytics and Sentinel cost estimate
            — no technical knowledge required.
          </p>
        </div>

        {/* Step indicator */}
        <StepIndicator
          currentStep={step}
          completedSteps={completedSteps}
          onStepClick={handleStepClick}
        />

        {/* Step content */}
        <div className="border rounded-xl p-6" style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)' }}>
          {step === 1 && (
            <Step1Organisation state={state} onChange={handleChange} onNext={goNext} />
          )}
          {step === 2 && (
            <Step2Infrastructure state={state} onChange={handleChange} onNext={goNext} onBack={goBack} />
          )}
          {step === 3 && (
            <Step3MonitoringNeeds state={state} onChange={handleChange} onNext={goNext} onBack={goBack} />
          )}
          {step === 4 && (
            <Step4Compliance state={state} onChange={handleChange} onNext={goNext} onBack={goBack} />
          )}
          {step === 5 && (
            <Step5Results state={state} onReset={handleReset} />
          )}
        </div>
      </div>
    </div>
  );
}
